import { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { api, setAccessToken } from '../services/api';

const AuthContext = createContext(null);

/**
 * Décode le payload d'un JWT (sans vérification de signature côté client)
 */
function decodeJwtPayload(token) {
  try {
    const base64Url = token.split('.')[1];
    const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
    return JSON.parse(atob(base64));
  } catch {
    return null;
  }
}

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  // Récupérer la session au montage via le refresh token cookie
  useEffect(() => {
    let cancelled = false;

    async function initAuth() {
      try {
        const data = await api.post('/auth/refresh');
        if (cancelled) return;

        setAccessToken(data.data.accessToken);

        // Le refresh ne renvoie que le token (sans displayName) → on hydrate
        // l'utilisateur complet via /auth/me. Fallback : décodage du JWT.
        try {
          const me = await api.get('/auth/me');
          if (cancelled) return;
          setUser(me.data.user);
        } catch {
          const payload = decodeJwtPayload(data.data.accessToken);
          if (payload) {
            setUser({
              id: payload.userId,
              email: payload.email,
              role: payload.role,
            });
          }
        }
      } catch {
        // Pas de session valide — on reste déconnecté
      } finally {
        if (!cancelled) setLoading(false);
      }
    }

    initAuth();
    return () => { cancelled = true; };
  }, []);

  const login = useCallback(async (email, password) => {
    const data = await api.post('/auth/login', { email, password });
    setAccessToken(data.data.accessToken);
    setUser(data.data.user);
    return data.data;
  }, []);

  const register = useCallback(async ({ email, password, displayName, role }) => {
    const data = await api.post('/auth/register', { email, password, displayName, role });
    setAccessToken(data.data.accessToken);
    setUser(data.data.user);
    return data.data;
  }, []);

  const logout = useCallback(async () => {
    try {
      await api.post('/auth/logout');
    } catch {
      // On déconnecte quand même côté client
    }
    setAccessToken(null);
    setUser(null);
  }, []);

  const value = {
    user,
    loading,
    login,
    register,
    logout,
    isAuthenticated: !!user,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth doit être utilisé dans un AuthProvider');
  }
  return context;
}

export default AuthContext;
